import { invalidateLocationQueries } from '@/lib/locations/invalidate';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApiQuery } from '../useApiQuery';
import { apiDelete, apiGet, apiPatch, apiPost, operationsClient } from '@/lib/api';
import { locationKeys } from '@/lib/queryKeys';

/** What a person is for at a site. */
export type LocationContactRole = 'SERVICE' | 'BILLING' | 'APPROVER' | 'OTHER';

/**
 * One entry per thing a customer actually receives, so the property manager can
 * take the "tech is on the way" text and nothing else while accounts payable
 * takes invoices and receipts. An EMPTY list means "all of them", so a newly
 * added kind never silently unsubscribes anyone.
 */
export type LocationContactNotificationType =
  | 'SCHEDULING'
  | 'REMINDER'
  | 'DISPATCH'
  | 'JOB_UPDATE'
  | 'ESTIMATE_SENT'
  | 'INVOICE_SENT'
  | 'PAYMENT_RECEIPT'
  | 'REVIEW_REQUEST'
  | 'MARKETING';

export interface LocationContact {
  id: string;
  locationId: string;
  contactId: string;
  role: LocationContactRole;
  isPrimary: boolean;
  title: string | null;
  receivesNotifications: boolean;
  /** May still hold a coarse legacy name; read it through `subscribedTypes`. */
  notificationTypes: string[];
  /** Which of their channels we may use here. EMPTY MEANS ALL of them. */
  notificationChannels: string[];
  notes: string | null;
  contact: {
    id: string;
    displayName: string | null;
    firstName: string | null;
    lastName: string | null;
    companyName: string | null;
    primaryPhone: string | null;
    primaryEmail: string | null;
    doNotContact: boolean | null;
    /** The whole-system switches: an opt-out here outranks site and job. */
    smsFollowUp: boolean | null;
    emailFollowUp: boolean | null;
  };
}

/** One person the system would notify, and why — the send-time answer. */
export interface ResolvedRecipient {
  contactId: string;
  role: LocationContactRole | 'ACCOUNT';
  isPrimary: boolean;
  displayName: string;
  title: string | null;
  /** Null when that channel is switched off for them. */
  phone: string | null;
  email: string | null;
  channels: ('SMS' | 'EMAIL')[];
  /** True when nobody is named at the site and we fell back to the account. */
  viaFallback: boolean;
}

const contactKeys = {
  list: (locationId: string) => [...locationKeys.detail(locationId), 'contacts'] as const,
  recipients: (locationId: string, type: string) =>
    [...locationKeys.detail(locationId), 'recipients', type] as const,
};

export function useLocationContacts(locationId: string | undefined) {
  return useApiQuery<LocationContact[]>({
    queryKey: contactKeys.list(locationId ?? 'none'),
    queryFn: () => apiGet<LocationContact[]>(operationsClient, `/locations/${locationId}/contacts`),
    enabled: !!locationId,
  });
}

/** Who would actually be messaged — used to show the fallback honestly. */
export function useLocationRecipients(
  locationId: string | undefined,
  type: LocationContactNotificationType,
) {
  return useApiQuery<ResolvedRecipient[]>({
    queryKey: contactKeys.recipients(locationId ?? 'none', type),
    queryFn: () =>
      apiGet<ResolvedRecipient[]>(
        operationsClient,
        `/locations/${locationId}/contacts/recipients?type=${type}`,
      ),
    enabled: !!locationId,
  });
}

function useInvalidate(locationId: string) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: locationKeys.detail(locationId) });
    // The contact's own Locations list names the people at each place, so
    // attaching somebody changes it too. See `invalidateLocationQueries`.
    invalidateLocationQueries(qc);
  };
}

export function useAddLocationContact(locationId: string) {
  const invalidate = useInvalidate(locationId);
  return useMutation({
    mutationFn: (body: {
      contactId: string;
      role?: LocationContactRole;
      /** Every role they hold here; the server writes one row per role. */
      roles?: LocationContactRole[];
      isPrimary?: boolean;
      title?: string;
      receivesNotifications?: boolean;
      notificationTypes?: LocationContactNotificationType[];
      notificationChannels?: ('SMS' | 'EMAIL')[];
    }) => apiPost<LocationContact>(operationsClient, `/locations/${locationId}/contacts`, body),
    onSuccess: invalidate,
  });
}

export function useUpdateLocationContact(locationId: string) {
  const invalidate = useInvalidate(locationId);
  return useMutation({
    mutationFn: ({
      id,
      ...body
    }: {
      id: string;
      isPrimary?: boolean;
      title?: string;
      receivesNotifications?: boolean;
      notificationTypes?: LocationContactNotificationType[];
      notificationChannels?: ('SMS' | 'EMAIL')[];
    }) =>
      apiPatch<LocationContact>(operationsClient, `/locations/${locationId}/contacts/${id}`, body),
    onSuccess: invalidate,
  });
}

export function useRemoveLocationContact(locationId: string) {
  const invalidate = useInvalidate(locationId);
  return useMutation({
    mutationFn: (id: string) =>
      apiDelete(operationsClient, `/locations/${locationId}/contacts/${id}`),
    onSuccess: invalidate,
  });
}

/** Human labels, kept next to the types they describe. */
export const ROLE_LABEL: Record<LocationContactRole, string> = {
  SERVICE: 'Service contact',
  BILLING: 'Billing contact',
  APPROVER: 'Approver',
  OTHER: 'Other contact',
};

export const NOTIFICATION_LABEL: Record<LocationContactNotificationType, string> = {
  SCHEDULING: 'Booked, rescheduled or cancelled',
  REMINDER: 'Appointment reminder',
  DISPATCH: 'Technician on the way',
  JOB_UPDATE: 'Job updates and completion',
  ESTIMATE_SENT: 'Estimates and proposals',
  INVOICE_SENT: 'Invoices and balance due',
  PAYMENT_RECEIPT: 'Payment receipts',
  REVIEW_REQUEST: 'Review requests',
  MARKETING: 'Offers and campaigns',
};

/** The order and grouping the notification list is shown in. */
export const NOTIFICATION_GROUPS: {
  label: string;
  types: LocationContactNotificationType[];
}[] = [
  { label: 'Scheduling', types: ['SCHEDULING', 'REMINDER', 'DISPATCH', 'JOB_UPDATE'] },
  { label: 'Sales', types: ['ESTIMATE_SENT'] },
  { label: 'Billing', types: ['INVOICE_SENT', 'PAYMENT_RECEIPT'] },
  { label: 'Follow-up', types: ['REVIEW_REQUEST', 'MARKETING'] },
];

export const NOTIFICATION_TYPES: LocationContactNotificationType[] = NOTIFICATION_GROUPS.flatMap(
  (g) => g.types,
);

/** Which roles are asked for a kind — mirrors ROLES_FOR_NOTIFICATION server-side. */
export const ROLES_FOR_NOTIFICATION: Record<
  LocationContactNotificationType,
  LocationContactRole[]
> = {
  SCHEDULING: ['SERVICE'],
  REMINDER: ['SERVICE'],
  DISPATCH: ['SERVICE'],
  JOB_UPDATE: ['SERVICE'],
  ESTIMATE_SENT: ['SERVICE', 'APPROVER'],
  INVOICE_SENT: ['BILLING'],
  PAYMENT_RECEIPT: ['BILLING'],
  REVIEW_REQUEST: ['SERVICE'],
  MARKETING: ['SERVICE', 'APPROVER'],
};

/** The coarse names older rows may hold, and what they cover. */
const LEGACY_GROUPS: Record<string, LocationContactNotificationType[]> = {
  APPOINTMENT: ['SCHEDULING', 'REMINDER', 'DISPATCH', 'JOB_UPDATE'],
  ESTIMATE: ['ESTIMATE_SENT'],
  INVOICE: ['INVOICE_SENT', 'PAYMENT_RECEIPT'],
  OUTREACH: ['REVIEW_REQUEST', 'MARKETING'],
};

/**
 * The kinds a row is actually subscribed to. An empty stored list means every
 * kind, and a legacy group name expands to the kinds it covered, so a row
 * written by an older build renders as the boxes it really implies.
 */
export function subscribedTypes(stored: string[]): LocationContactNotificationType[] {
  if (stored.length === 0) return [...NOTIFICATION_TYPES];
  const out = new Set<LocationContactNotificationType>();
  for (const value of stored) {
    if ((NOTIFICATION_TYPES as string[]).includes(value)) {
      out.add(value as LocationContactNotificationType);
      continue;
    }
    for (const expanded of LEGACY_GROUPS[value] ?? []) out.add(expanded);
  }
  return NOTIFICATION_TYPES.filter((t) => out.has(t));
}

/**
 * Tick or untick one kind. Everything selected is stored as the empty list, so
 * a kind added later reaches this person too instead of arriving pre-muted.
 */
export function toggleNotificationType(
  stored: string[],
  type: LocationContactNotificationType,
): LocationContactNotificationType[] {
  const current = subscribedTypes(stored);
  const next = current.includes(type) ? current.filter((t) => t !== type) : [...current, type];
  return next.length === NOTIFICATION_TYPES.length
    ? []
    : NOTIFICATION_TYPES.filter((t) => next.includes(t));
}

/** A short "what they get" line: the whole list, a group, or the kinds named. */
export function describeSubscription(stored: string[]): string {
  const types = subscribedTypes(stored);
  if (types.length === NOTIFICATION_TYPES.length) return 'Everything';
  if (types.length === 0) return 'Nothing selected';
  const whole = NOTIFICATION_GROUPS.filter(
    (g) => g.types.every((t) => types.includes(t)) && g.types.length > 1,
  );
  const covered = new Set(whole.flatMap((g) => g.types));
  const rest = types.filter((t) => !covered.has(t));
  return [...whole.map((g) => g.label), ...rest.map((t) => NOTIFICATION_LABEL[t])].join(', ');
}

/**
 * The state of one channel for a person AT A SITE, in the shape the bells
 * take. Mirrors the server's `blockedBy`: the contact record is the outer
 * gate, the site may narrow it, and a channel they do not have is never on.
 */
export function siteChannelState(row: LocationContact): {
  channel: 'SMS' | 'EMAIL';
  enabled: boolean;
  blockedBy: 'no-channel' | 'contact' | 'site' | 'job' | null;
  overriddenHere: boolean;
}[] {
  const { contact } = row;
  const decide = (channel: 'SMS' | 'EMAIL', value: string | null, followUp: boolean | null) => {
    let blockedBy: 'no-channel' | 'contact' | 'site' | null = null;
    if (!value) blockedBy = 'no-channel';
    else if (contact.doNotContact || followUp === false) blockedBy = 'contact';
    else if (!row.receivesNotifications) blockedBy = 'site';
    else if (row.notificationChannels.length > 0 && !row.notificationChannels.includes(channel))
      blockedBy = 'site';
    return {
      channel,
      enabled: blockedBy === null,
      blockedBy,
      overriddenHere: row.notificationChannels.length > 0,
    };
  };
  return [
    decide('SMS', contact.primaryPhone, contact.smsFollowUp),
    decide('EMAIL', contact.primaryEmail, contact.emailFollowUp),
  ];
}

/** Build a site preference update without confusing no channels with the all-channels default. */
export function toggleSiteChannel(
  stored: { receivesNotifications: boolean; notificationChannels: string[] },
  channel: 'SMS' | 'EMAIL',
  enabled: boolean,
): { receivesNotifications: boolean; notificationChannels: string[]; notificationTypes: string[] } {
  const all = ['SMS', 'EMAIL'];
  const current = !stored.receivesNotifications
    ? []
    : stored.notificationChannels.length === 0
      ? all
      : stored.notificationChannels;
  const next = enabled
    ? all.filter((c) => current.includes(c) || c === channel)
    : current.filter((c) => c !== channel);
  return {
    receivesNotifications: next.length > 0,
    notificationChannels: next.length === all.length ? [] : next,
    notificationTypes: [],
  };
}
